import { atom, getDefaultStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { DictateMapFunctionalityEntryPoint } from '../../contracts/dictate-device-setup-analytics.types';
import { isDictationDeviceConfigured } from './mappings';

/**
 * Device key of a newly connected dictaphone waiting to be queued onto the
 * modal queue. `null` = nothing armed. The app-side launch row turns this into
 * the awareness-card queue item; per-device suppression lives in
 * `dictaphoneSetupAwarenessDismissedKeysAtom`, not in backend `seen_modals`.
 */
export const dictaphoneSetupAwarenessTriggerAtom = atom<string | null>(null);

/** Dev-tools preview override; wins over {@link dictaphoneSetupAwarenessTriggerAtom}. */
export const dictaphoneSetupAwarenessDevTriggerOverrideAtom = atom<string | null>(null);

export type DictaphoneSetupAwarenessAction = 'dismiss' | 'engage';

/**
 * Which rollout the card ran under: `detected` is the phase-1 popup (Got it
 * only), `setup` is phase 2 (Set up now offered).
 */
export type DictaphoneSetupAwarenessPhase = 'detected' | 'setup';

/**
 * Per-device dismiss/engage memory, keyed by hardware device key. Survives app
 * relaunches via localStorage — a different newly connected device re-triggers,
 * the same device never re-prompts within a phase. A phase-1 dismiss does not
 * suppress the phase-2 card, which offers setup the phase-1 card never did.
 * Records written before `phase` existed are phase 1.
 */
export const dictaphoneSetupAwarenessDismissedKeysAtom = atomWithStorage<
  Record<
    string,
    {
      at: string;
      action: DictaphoneSetupAwarenessAction;
      phase?: DictaphoneSetupAwarenessPhase;
    }
  >
>(
  'heidi.dictate.usbDictaphoneSetup.dismissedKeys:v1',
  {},
  createJSONStorage(createLocalStorage)
);

/**
 * Session debounce: device keys already offered or shown this tab. Reconnect
 * churn (USB re-plug, sleep/wake) must not re-offer the same device within one
 * app session. Not persisted — a connect after relaunch is a real connect.
 */
export const dictaphoneSetupAwarenessOfferedThisSessionAtom = atom<Set<string>>(
  new Set<string>()
);

function createLocalStorage(): Storage {
  if (typeof window === 'undefined') {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
      clear: () => undefined,
      key: () => null,
      length: 0,
    };
  }
  return window.localStorage;
}

const isDismissedOrOffered = (
  deviceKey: string,
  phase: DictaphoneSetupAwarenessPhase
): boolean => {
  const store = getDefaultStore();
  const record = store.get(dictaphoneSetupAwarenessDismissedKeysAtom)[deviceKey];
  if (record) {
    const recordPhase = record.phase ?? 'detected';
    if (phase === 'detected' || recordPhase === 'setup') return true;
  }
  return store.get(dictaphoneSetupAwarenessOfferedThisSessionAtom).has(deviceKey);
};

/**
 * Arm the awareness card for a newly connected device. No-op when the device
 * was already dismissed/engaged (in phase 2, only a phase-2 outcome counts),
 * already offered this session, or already configured. First write wins — a
 * second device arriving while one is armed is declined (returns false) and
 * NOT recorded as offered: it was never shown, so the session debounce must
 * not suppress it once the armed card is consumed. Returns whether this call
 * armed the card.
 */
export const armDictaphoneSetupAwareness = (
  deviceKey: string,
  phase: DictaphoneSetupAwarenessPhase
): boolean => {
  const store = getDefaultStore();
  if (isDismissedOrOffered(deviceKey, phase)) return false;
  if (isDictationDeviceConfigured(deviceKey)) return false;
  if (store.get(dictaphoneSetupAwarenessTriggerAtom) !== null) return false;
  store.set(dictaphoneSetupAwarenessOfferedThisSessionAtom, (prev) => {
    const next = new Set(prev);
    next.add(deviceKey);
    return next;
  });
  store.set(dictaphoneSetupAwarenessTriggerAtom, deviceKey);
  return true;
};

export const clearDictaphoneSetupAwarenessTrigger = (): void => {
  getDefaultStore().set(dictaphoneSetupAwarenessTriggerAtom, null);
};

/** Record the per-device outcome so the same device never re-prompts in this phase. */
export const rememberDictaphoneSetupAwareness = (
  deviceKey: string,
  action: DictaphoneSetupAwarenessAction,
  phase: DictaphoneSetupAwarenessPhase
): void => {
  getDefaultStore().set(dictaphoneSetupAwarenessDismissedKeysAtom, (prev) => ({
    ...prev,
    [deviceKey]: { at: new Date().toISOString(), action, phase },
  }));
};

export type DictateAddDeviceDialogSource = 'awareness' | 'settings' | 'dev';

export type DictateAddDeviceDialogRequest = {
  /** Preferred hardware key from the opener; null = search all. */
  deviceKey: string | null;
  source: DictateAddDeviceDialogSource;
};

/**
 * Add-device dialog request. `null` = closed. Set by the awareness card,
 * Settings Dictaphone row, or dev tools. The dialog records the per-device
 * `engage` outcome on Setup my device — an unconsumed request leaves no
 * persisted memory.
 */
export const dictateAddDeviceDialogRequestAtom =
  atom<DictateAddDeviceDialogRequest | null>(null);

/**
 * Map-functionality dialog request (SCR-7479). `null` = closed. 7477 writes
 * this on Setup my device / Configure; mapping chrome is a sibling slice.
 *
 * `source` is REQUIRED so no opener can silently omit it: it exists only to
 * report which surface opened the dialog, and the Settings row reaches here
 * without passing through the add-device wizard, the only other surface that
 * records an entry point.
 */
export const dictateMapFunctionalityDialogRequestAtom = atom<{
  deviceKey: string;
  source: DictateMapFunctionalityEntryPoint;
} | null>(null);
